"use client"
import { useEffect, type RefObject } from 'react'
import { type PlanId } from '@/lib/branchTypes'
import { PLAYBOOK_BRANCH_FLOWCHART_ID } from '../choicePointAnchor'
import {
  PLAN_A_PREVIEW_ELEMENT_ID,
  PLAN_A_PREVIEW_FADE_END_VH,
} from './PlanAPreviewSubtree'

// Until the reader picks a plan, the content column is height-capped and
// overflow-hidden so scrolling stops just past the choice point. The browser
// handles the boundary natively — no event interception, no overshoot.
//
// The cap normally lands at the Plan A preview's fade-end (see applyCap); this
// is the fallback distance when no preview is present (non-Plan-A branches).
export const CHOICE_POINT_SCROLL_LOCK_MAX_PX = 1250

type Props = {
  selectedPlan: PlanId | null
  contentSectionRef: RefObject<HTMLDivElement | null>
}

export const ChoicePointScrollLock = ({ selectedPlan, contentSectionRef }: Props) => {
  useEffect(() => {
    const section = contentSectionRef.current
    if (!section) return
    if (selectedPlan !== null) return

    const previousMaxHeight = section.style.maxHeight
    const previousOverflow = section.style.overflow

    // Engages once scrolled to/past the choice point, releases above it.
    // The threshold (flowchartTop) sits above the clipped region, so
    // toggling the cap can't move it — no oscillation.
    let capEngaged = false

    const getFlowchartTop = () => {
      const flowchart = document.getElementById(PLAYBOOK_BRANCH_FLOWCHART_ID)
      if (!flowchart) return null
      return flowchart.getBoundingClientRect().top + window.scrollY
    }

    const applyCap = () => {
      const flowchart = document.getElementById(PLAYBOOK_BRANCH_FLOWCHART_ID)
      if (!flowchart) return
      const sectionTop = section.getBoundingClientRect().top + window.scrollY
      const offsetInSection = (el: HTMLElement) =>
        el.getBoundingClientRect().top + window.scrollY - sectionTop

      // Cap where the Plan A preview fade hits full transparency, so the
      // scroll region ends at the bottom of the visible text with no trailing
      // whitespace. Without a preview, fall back to a fixed distance.
      const preview = document.getElementById(PLAN_A_PREVIEW_ELEMENT_ID)
      const capPx = preview
        ? offsetInSection(preview) +
          (PLAN_A_PREVIEW_FADE_END_VH / 100) * window.innerHeight
        : offsetInSection(flowchart) + CHOICE_POINT_SCROLL_LOCK_MAX_PX

      section.style.maxHeight = `${capPx}px`
      section.style.overflow = 'hidden'
      capEngaged = true

      // Shrinking scrollHeight makes the browser clamp scrollY upward on its
      // own — the visible flicker, and (after cmd-down) a clamp that can land
      // above flowchartTop and trip release → regrow → bounce. Pre-clamp to
      // the new bottom ourselves so there's no second correction.
      const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight
      if (window.scrollY > maxScrollTop) window.scrollTo(0, maxScrollTop)
    }

    const releaseCap = () => {
      section.style.maxHeight = previousMaxHeight
      section.style.overflow = previousOverflow
      capEngaged = false
    }

    const handleScroll = () => {
      const flowchartTop = getFlowchartTop()
      if (flowchartTop === null) return
      // Hysteresis: engage at the choice point, release only after a full
      // viewport back above it. Without the gap, the post-cap upward clamp
      // can itself drop below flowchartTop and release → regrow → bounce.
      if (!capEngaged && window.scrollY >= flowchartTop) applyCap()
      else if (capEngaged && window.scrollY < flowchartTop - window.innerHeight) releaseCap()
    }

    // Handle loading already scrolled past the choice point.
    handleScroll()

    // Content above can reflow (resize, font/image load) and move the
    // choice point's offset; recompute only if the cap is engaged.
    const handleReflow = () => {
      if (capEngaged) applyCap()
    }
    const resizeObserver = new ResizeObserver(handleReflow)
    resizeObserver.observe(section)
    window.addEventListener('resize', handleReflow)
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleReflow)
      window.removeEventListener('scroll', handleScroll)
      section.style.maxHeight = previousMaxHeight
      section.style.overflow = previousOverflow
    }
  }, [selectedPlan, contentSectionRef])

  return null
}
